import React, { useCallback, useMemo, useState } from "react";
import Typography from "@material-ui/core/Typography";
import { Box, Grid, makeStyles } from "@material-ui/core";

import ContractorDialog from "./ContractorDialog";
import { asCurrency } from "../../../../../utils";
import {
  REPORT_ITEM_SUB_TYPE,
  REPORT_ITEM_TYPE
} from "../../../../../constants";
import CustomCardHeader from "../../CustomCardHeader";
import Button from "components/CustomButtons/Button";
import { Close, Edit } from "@material-ui/icons";
import Loading from "../../../../common/Loading";

const useStyles = makeStyles(() => ({
  name: {
    fontSize: "1rem"
  },
  position: {
    fontSize: "0.75rem"
  },
  flexGrow: {
    flexGrow: 1
  },
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
}));
const ContractorExpense = ({
  reportID,
  data,
  chapterID,
  year,
  createReportItem,
  updateReportItem,
  initialReportItems,
  removeReportItem,
  onRefetch,
  onHelp,
  onRedirect,
  isEditable
}) => {
  const classes = useStyles();

  const [activeData, setActiveData] = useState({
    item: {},
    isAdmin: false,
    isCreating: true
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const administrators = useMemo(
    () =>
      data.filter(
        item => item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.ADMINISTRATOR
      ),
    [data]
  );

  const onCreate = useCallback(
    async (item, isAdmin) => {
      const { hoursPerWeek, amount, ...itemData } = item;

      let body = {
        hoursPerWeek: +hoursPerWeek,
        amount: +amount,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.ADMINISTRATOR,
        reportItemTypeID: REPORT_ITEM_TYPE.EXPENSE,
        officer: { ...itemData, chapterID }
      };

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: [body]
        });
        onRedirect(_reportID);
      } else {
        await createReportItem({ ...body, reportID });
      }

      onRefetch();
      setDialogOpen(false);
    },
    [reportID, activeData, chapterID]
  );

  const onUpdate = useCallback(
    async (item, isAdmin) => {
      const { hoursPerWeek, amount, ...itemData } = item;

      let body = {
        hoursPerWeek: +hoursPerWeek,
        amount: +amount,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.ADMINISTRATOR,
        reportItemTypeID: REPORT_ITEM_TYPE.EXPENSE,
        officer: itemData
      };

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: [body]
        });
        onRedirect(_reportID);
      } else {
        await updateReportItem(activeData.item.id, { ...body, reportID });
      }

      onRefetch();
      setDialogOpen(false);
    },
    [reportID, activeData]
  );

  const openCreateDialog = useCallback(isAdmin => {
    setActiveData({
      item: {},
      isAdmin,
      isCreating: true
    });
    setDialogOpen(true);
  }, []);

  const openUpdateDialog = useCallback((item, isAdmin) => {
    setActiveData({
      item,
      isAdmin,
      isCreating: false
    });
    setDialogOpen(true);
  }, []);

  const [isDirty, setDirty] = useState(false);

  const onRemove = useCallback(async item => {
    setDirty(true);
    await removeReportItem(item.id);
    onRefetch();
  }, []);

  return (
    <Box mb={3}>
      <CustomCardHeader
        label={"Employee, Administrator, Bookkeeper & Other Contractor Data"}
        onHelp={() =>
          onHelp(REPORT_ITEM_TYPE.EXPENSE, REPORT_ITEM_SUB_TYPE.ADMINISTRATOR)
        }
      />
      {isDirty ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Loading />
        </div>
      ) : (
        <>
          {" "}
          {administrators.map(
            (item, index) =>
              item && (
                <Box px={1} py={0.5} key={`officer-${index}`}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={8}>
                      <Typography className={classes.name}>
                        {item.administrator.firstName}&nbsp;
                        {item.administrator.lastName}
                      </Typography>
                      <Typography className={classes.position}>
                        {item.administrator.position}{" "}
                        {item.administrator.employmentType && (
                          <>({item.administrator.employmentType})</>
                        )}
                      </Typography>
                    </Grid>
                    <Grid item className={classes.flexGrow}>
                      <Typography align="right">
                        {asCurrency(item.amount)}
                      </Typography>
                    </Grid>
                    {isEditable && (
                      <Grid item>
                        <Box display="flex">
                          <Button
                            simple
                            color="success"
                            className={classes.actions}
                            onClick={() => openUpdateDialog(item, false)}
                          >
                            <Edit />
                          </Button>
                          <Button
                            simple
                            color="danger"
                            onClick={() => onRemove(item)}
                            className={classes.actions}
                          >
                            <Close />
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )
          )}
        </>
      )}

      {isEditable && (
        <Box ml={3} mt={2}>
          <a onClick={() => openCreateDialog(false)}>
            <strong>Add an Employee, Bookkeeper or Contractor</strong>
          </a>
        </Box>
      )}

      <ContractorDialog
        open={dialogOpen}
        data={activeData}
        onClose={() => setDialogOpen(false)}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />
    </Box>
  );
};

export default ContractorExpense;
